// Helpers for measuring intrinsic media dimensions and grabbing a frame
// thumbnail from a video file before placing it on the canvas.

const DEFAULT_MAX_W = 380;
const DEFAULT_MAX_H = 480;
const FALLBACK = { width: 320, height: 320, naturalW: 320, naturalH: 320 };

function fit(naturalW, naturalH, maxW = DEFAULT_MAX_W, maxH = DEFAULT_MAX_H) {
  if (!naturalW || !naturalH) return FALLBACK;
  const ratio = naturalW / naturalH;
  let w = maxW;
  let h = w / ratio;
  if (h > maxH) {
    h = maxH;
    w = h * ratio;
  }
  return {
    width: Math.round(w),
    height: Math.round(h),
    naturalW,
    naturalH,
  };
}

export function measureImage(url, opts = {}) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () =>
      resolve(fit(img.naturalWidth, img.naturalHeight, opts.maxW, opts.maxH));
    img.onerror = () => resolve(FALLBACK);
    img.src = url;
  });
}

export function measureVideo(url, opts = {}) {
  return new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.onloadedmetadata = () =>
      resolve(fit(v.videoWidth, v.videoHeight, opts.maxW, opts.maxH));
    v.onerror = () => resolve(FALLBACK);
    v.src = url;
  });
}

export async function measureAny(type, url, opts) {
  if (!url) return FALLBACK;
  if (type === "video") return measureVideo(url, opts);
  return measureImage(url, opts);
}

/**
 * Capture the currently-displayed frame from a live <video> element. Returns
 * a JPEG Blob (or null on failure). The video must already be loaded enough
 * to have a videoWidth/videoHeight; the caller decides which `currentTime`
 * to capture by setting it on the element first and waiting for `seeked`.
 */
export function captureCurrentFrame(videoEl, opts = {}) {
  return new Promise((resolve) => {
    if (!videoEl || !videoEl.videoWidth || !videoEl.videoHeight) {
      resolve(null);
      return;
    }
    try {
      const w = videoEl.videoWidth;
      const h = videoEl.videoHeight;
      const maxSide = opts.maxSide || 720;
      const ratio = w / h;
      let cw = w, ch = h;
      if (cw > maxSide) {
        cw = maxSide;
        ch = Math.round(cw / ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoEl, 0, 0, cw, ch);
      canvas.toBlob(
        (blob) => resolve(blob || null),
        "image/jpeg",
        opts.quality || 0.85,
      );
    } catch {
      resolve(null);
    }
  });
}
export function extractVideoThumbnail(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = "anonymous";

    const cleanup = () => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        // ignore
      }
    };

    let seeked = false;
    const failTimer = setTimeout(() => {
      cleanup();
      resolve(null);
    }, 15000);

    video.addEventListener("loadedmetadata", () => {
      const target = Math.min(1, (video.duration || 1) * 0.1);
      try {
        video.currentTime = target;
      } catch {
        // fall through
      }
    });

    video.addEventListener("seeked", () => {
      if (seeked) return;
      seeked = true;
      try {
        const w = video.videoWidth || 1280;
        const h = video.videoHeight || 720;
        const canvas = document.createElement("canvas");
        // Cap thumbnail size to keep it small.
        const maxSide = 720;
        const ratio = w / h;
        let cw = w, ch = h;
        if (cw > maxSide) {
          cw = maxSide;
          ch = Math.round(cw / ratio);
        }
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, cw, ch);
        canvas.toBlob(
          (blob) => {
            clearTimeout(failTimer);
            cleanup();
            resolve(blob || null);
          },
          "image/jpeg",
          0.85,
        );
      } catch {
        clearTimeout(failTimer);
        cleanup();
        resolve(null);
      }
    });

    video.addEventListener("error", () => {
      clearTimeout(failTimer);
      cleanup();
      resolve(null);
    });

    video.src = url;
  });
}
